/**
 * Typed client for the per-account settlement-rail preference
 * (ADR-0081):
 *
 *   GET /v1/account/settlement-rail  — owner/admin reads the current
 *       preference + which rails are connected (so the UI can disable
 *       unconnected options).
 *   PUT /v1/account/settlement-rail  — owner/admin sets a new rail.
 *       Rejected `rail_not_connected` (409) when the chosen rail has no
 *       connected integration for the account.
 *
 * Wire body is `{ rail: 'stripe' | 'shopify' }` — **snake_case** values
 * per CLAUDE.md §3. The preference is the per-account enum the rail
 * selector reads before routing a `place_order`; selecting a rail with
 * no connector would re-create the silent place_order abort, so the
 * server hard-gates it and the dashboard disables unconnected options.
 */

import { ApiError, type HttpClient } from './http-client.js';

export type SettlementRail = 'stripe' | 'shopify';

export interface ConnectedRails {
  readonly stripe: boolean;
  readonly shopify: boolean;
}

/**
 * GET result discriminated union.
 *   - `ok`        — server returned 200 with the rail + connected set.
 *   - `forbidden` — caller's role is reviewer/viewer (server 403).
 *   - `error`     — network / 5xx / bad shape.
 */
export type GetSettlementRailResult =
  | {
      readonly kind: 'ok';
      readonly rail: SettlementRail;
      readonly connected: ConnectedRails;
    }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * PUT result discriminated union.
 *   - `ok`                 — server accepted; echoes the new rail.
 *   - `forbidden`          — role gate (server 403).
 *   - `rail_not_connected` — the chosen rail has no connector (409).
 *   - `bad_request`        — enum validation failed (server 400).
 *   - `error`              — network / 5xx / bad shape.
 */
export type PutSettlementRailResult =
  | { readonly kind: 'ok'; readonly rail: SettlementRail }
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'rail_not_connected'; readonly rail: SettlementRail }
  | { readonly kind: 'bad_request' }
  | { readonly kind: 'error'; readonly message: string };

/* ---------- internal wire shapes ---------- */

interface GetWire {
  readonly rail: string;
  readonly connected?: { stripe?: unknown; shopify?: unknown };
}

interface PutWire {
  readonly rail: string;
}

/* --------------------------------------------------------------------- */

export async function getSettlementRail(
  http: HttpClient,
): Promise<GetSettlementRailResult> {
  try {
    const res = await http.request<GetWire>({
      method: 'GET',
      path: '/v1/account/settlement-rail',
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const rail = parseRail(res.rail);
    if (rail === null) {
      return {
        kind: 'error',
        message: 'Server returned an unknown rail value.',
      };
    }
    return {
      kind: 'ok',
      rail,
      connected: {
        stripe: res.connected?.stripe === true,
        shopify: res.connected?.shopify === true,
      },
    };
  } catch (err) {
    if (err instanceof ApiError) {
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

export async function putSettlementRail(
  http: HttpClient,
  rail: SettlementRail,
): Promise<PutSettlementRailResult> {
  try {
    const res = await http.request<PutWire>({
      method: 'PUT',
      path: '/v1/account/settlement-rail',
      body: { rail },
    });
    if (res === undefined) {
      return { kind: 'error', message: 'Empty response body.' };
    }
    const echoed = parseRail(res.rail);
    if (echoed === null) {
      return {
        kind: 'error',
        message: 'Server returned an unknown rail value.',
      };
    }
    return { kind: 'ok', rail: echoed };
  } catch (err) {
    if (err instanceof ApiError) {
      // 409 + `rail_not_connected` reason — the load-bearing gate.
      if (err.kind === 'conflict' && err.server_reason === 'rail_not_connected') {
        return { kind: 'rail_not_connected', rail };
      }
      if (err.kind === 'forbidden') return { kind: 'forbidden' };
      if (err.kind === 'bad_request') return { kind: 'bad_request' };
      return { kind: 'error', message: explainApiError(err) };
    }
    return {
      kind: 'error',
      message: err instanceof Error ? err.message : 'Network error.',
    };
  }
}

/* ---------- helpers ---------- */

function parseRail(raw: string | undefined): SettlementRail | null {
  return raw === 'stripe' || raw === 'shopify' ? raw : null;
}

function explainApiError(err: ApiError): string {
  switch (err.kind) {
    case 'service_unavailable':
      return 'Settlement-rail service is temporarily unavailable.';
    case 'network_timeout':
      return 'This may have saved — refresh to check before trying again.';
    case 'network_error':
      return 'Network error — check your connection.';
    case 'bad_response':
      return 'Server returned an unexpected response.';
    default:
      return err.message;
  }
}
